import { useEffect, useMemo, useState } from "react";
import { SortableList } from "../../../components/ui/Sortable";
import { useQueryClient } from "@tanstack/react-query";
import {
  PERMISSION_GROUPS,
  PERMISSION_INFO,
  Permission,
  ROLE_PRESETS,
  hasPermission,
  type CommunityRole,
  type CommunityStructure,
  type Member,
  type RolePreset,
} from "@atrium/shared";
import { api, errorMessage } from "../../../lib/api";
import { cn } from "../../../lib/cn";
import { ROLE_COLOURS } from "../../../lib/palette";
import { Avatar } from "../../../components/ui/Avatar";
import { Button, IconButton } from "../../../components/ui/Button";
import { Segmented, Switch } from "../../../components/ui/Controls";
import { Dialog, confirm } from "../../../components/ui/Dialog";
import { Field, Input } from "../../../components/ui/Input";
import { SettingsGroup, SettingsRow, UnsavedBar } from "../../../components/ui/Settings";
import { toast } from "../../../components/ui/Toast";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuTrigger } from "../../../components/ui/Menu";
import { IconBot, IconCheck, IconCrown, IconDanger, IconDrag, IconLock, IconMore, IconPlus, IconSearch, IconShield, IconTrash, IconUserMinus, IconUsers, IconX } from "../../../components/icons";
import { Tooltip } from "../../../components/ui/Tooltip";
import { useMembers } from "../hooks";
import { useRefreshCommunity, type SectionProps } from "./shared";

function myTopPosition(structure: CommunityStructure) {
  return structure.community.isOwner ? Infinity : Math.max(0, ...structure.roles.filter((r) => structure.myRoleIds.includes(r.id)).map((r) => r.position));
}

const isAdminPerms = (s: CommunityStructure) => s.community.isOwner || hasPermission(s.community.myPermissions, Permission.ADMINISTRATOR);

/* ───────────── Create with presets ───────────── */

function CreateRoleDialog({ open, onOpenChange, structure, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; structure: CommunityStructure; onCreated: (id: string) => void }) {
  const [preset, setPreset] = useState<RolePreset>(ROLE_PRESETS[0]);
  const [name, setName] = useState(ROLE_PRESETS[0].name);
  const [pending, setPending] = useState(false);
  const refresh = useRefreshCommunity(structure.community.id);
  const admin = isAdminPerms(structure);
  const canGrant = (p: RolePreset) => admin || (p.permissions & ~structure.community.myPermissions) === 0;

  useEffect(() => {
    if (open) {
      const first = ROLE_PRESETS.find(canGrant) ?? ROLE_PRESETS[ROLE_PRESETS.length - 1];
      setPreset(first);
      setName(first.name);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setPending(true);
    try {
      const r = await api.post<CommunityRole>(`/api/communities/${structure.community.id}/roles`, {
        name: name.trim() || preset.name,
        color: preset.color,
        permissions: preset.permissions,
        hoist: preset.hoist,
      });
      await refresh();
      onCreated(r.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create role"
      description="Start from a preset. You can fine-tune every permission afterwards."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={pending} onClick={() => void create()}>
            Create role
          </Button>
        </>
      }
    >
      <div role="radiogroup" aria-label="Role preset" className="grid gap-2 sm:grid-cols-2">
        {ROLE_PRESETS.map((p) => {
          const on = preset.key === p.key;
          const allowed = canGrant(p);
          const count = PERMISSION_INFO.filter((i) => hasPermission(p.permissions, Permission[i.key])).length;
          return (
            <button
              key={p.key}
              role="radio"
              aria-checked={on}
              disabled={!allowed}
              onClick={() => {
                if (name === preset.name || !name.trim()) setName(p.name);
                setPreset(p);
              }}
              className={cn(
                "flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors",
                on ? "border-accent bg-accent-soft" : "border-line hover:border-line-strong hover:bg-surface",
                !allowed && "cursor-not-allowed opacity-50",
                p.key === "custom" && "sm:col-span-2",
              )}
            >
              <span className="flex w-full items-center gap-2">
                <span className="size-3 rounded-full" style={{ background: p.color ?? "var(--fg-3)" }} aria-hidden />
                <span className="flex-1 text-sm font-semibold text-fg">{p.key === "custom" ? "Custom" : p.name}</span>
                {on && <IconCheck className="icon size-4 text-accent" aria-hidden />}
              </span>
              <span className="text-xs text-fg-3">{allowed ? p.description : "Includes permissions you don't have."}</span>
              {p.key !== "custom" && (
                <span className="text-xs text-fg-3 tabular">{p.key === "admin" ? "All permissions" : `${count} permissions`}</span>
              )}
            </button>
          );
        })}
      </div>
      <Field label="Name" className="mt-4">
        {(p) => <Input {...p} value={name} maxLength={32} onChange={(e) => setName(e.target.value)} />}
      </Field>
    </Dialog>
  );
}

/* ───────────── Editor tabs ───────────── */

function DisplayTab({ role, draft, setDraft, canEdit }: { role: CommunityRole; draft: Draft; setDraft: (d: Draft) => void; canEdit: boolean }) {
  const [hex, setHex] = useState("");
  if (role.isDefault)
    return <p className="text-sm text-fg-3">@everyone is the base role every member has. Its name and colour can't change; set its permissions in the next tab.</p>;
  const validHex = /^#[0-9a-fA-F]{6}$/.test(hex);
  return (
    <div className="space-y-6">
      <Field label="Name">{(p) => <Input {...p} value={draft.name} maxLength={32} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />}</Field>
      <div>
        <div className="mb-1 text-sm font-medium text-fg-2">Colour</div>
        <p className="mb-3 text-xs text-fg-3">Members use the colour of their highest coloured role on their name.</p>
        <div role="radiogroup" aria-label="Role colour" className="flex flex-wrap items-center gap-2">
          <button
            role="radio"
            aria-checked={draft.color === null}
            aria-label="No colour"
            disabled={!canEdit}
            onClick={() => setDraft({ ...draft, color: null })}
            className={cn("flex size-8 items-center justify-center rounded-full border border-line-strong text-fg-3", draft.color === null && "ring-2 ring-fg ring-offset-2 ring-offset-bg")}
          >
            <IconX className="icon size-4" />
          </button>
          {ROLE_COLOURS.map((c) => (
            <button
              key={c}
              role="radio"
              aria-checked={draft.color === c}
              aria-label={c}
              disabled={!canEdit}
              onClick={() => setDraft({ ...draft, color: c })}
              className={cn("flex size-8 items-center justify-center rounded-full", draft.color === c && "ring-2 ring-fg ring-offset-2 ring-offset-bg")}
              style={{ background: c }}
            >
              {draft.color === c && <IconCheck className="icon size-4 text-white" aria-hidden />}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <label className="relative size-8 shrink-0 cursor-pointer overflow-hidden rounded-full border border-line-strong" style={{ background: draft.color ?? "transparent" }}>
            <span className="sr-only">Pick any colour</span>
            <input
              type="color"
              disabled={!canEdit}
              value={draft.color ?? "#9ca3af"}
              onChange={(e) => setDraft({ ...draft, color: e.target.value })}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </label>
          <Input value={hex} onChange={(e) => setHex(e.target.value.trim())} placeholder={draft.color ?? "#hex"} aria-label="Hex colour" maxLength={7} disabled={!canEdit} className="!h-8 max-w-32 font-mono text-sm" />
          <Button size="sm" disabled={!validHex || !canEdit} onClick={() => (setDraft({ ...draft, color: hex.toLowerCase() }), setHex(""))}>
            Apply
          </Button>
        </div>
        <div className="mt-4 flex items-center gap-3 rounded-lg border border-line px-3 py-2.5">
          <Avatar src={null} name={draft.name || "Role"} size="sm" />
          <div className="text-sm">
            <span className="font-semibold" style={{ color: draft.color ?? undefined }}>
              {draft.name || "Role"} member
            </span>
            <span className="ml-2 text-xs text-fg-3">Today at 14:02</span>
            <div className="text-fg-2">This is how names look in chat.</div>
          </div>
        </div>
      </div>
      <SettingsGroup>
        <SettingsRow title="Show separately" description="List members with this role in their own group in the member list." htmlFor="role-hoist">
          <Switch id="role-hoist" label="Show separately" disabled={!canEdit} checked={draft.hoist} onCheckedChange={(v) => setDraft({ ...draft, hoist: v })} />
        </SettingsRow>
      </SettingsGroup>
    </div>
  );
}

function PermissionsTab({ role, structure, draft, setDraft, canEdit }: { role: CommunityRole; structure: CommunityStructure; draft: Draft; setDraft: (d: Draft) => void; canEdit: boolean }) {
  const [q, setQ] = useState("");
  const admin = isAdminPerms(structure);
  const mine = structure.community.myPermissions;
  const list = PERMISSION_INFO.filter((p) => !q || `${p.label} ${p.description}`.toLowerCase().includes(q.toLowerCase()));
  const isAdminRole = (draft.permissions & Permission.ADMINISTRATOR) !== 0;
  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1">
          <IconSearch className="icon pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search permissions" aria-label="Search permissions" className="!h-9 pl-8" />
        </div>
        {canEdit && (
          <Menu>
            <MenuTrigger asChild>
              <Button variant="outline">Presets</Button>
            </MenuTrigger>
            <MenuContent>
              <MenuLabel>Replace permissions with</MenuLabel>
              {ROLE_PRESETS.filter((p) => p.key !== "custom" && (!role.isDefault || p.key !== "admin")).map((p) => (
                <MenuItem key={p.key} disabled={!admin && (p.permissions & ~mine) !== 0} onSelect={() => setDraft({ ...draft, permissions: p.permissions })}>
                  {p.name}
                </MenuItem>
              ))}
              <MenuItem icon={IconX} onSelect={() => setDraft({ ...draft, permissions: 0 })}>
                Clear all
              </MenuItem>
            </MenuContent>
          </Menu>
        )}
      </div>
      {isAdminRole && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-surface px-3 py-2.5 text-sm text-fg-2">
          <IconDanger className="icon mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
          Administrator grants every permission in every channel and ignores channel overrides.
        </div>
      )}
      {PERMISSION_GROUPS.map((g) => {
        const items = list.filter((p) => p.group === g);
        if (!items.length) return null;
        return (
          <SettingsGroup key={g} title={g === "Text" ? "Text channels" : g === "Voice" ? "Voice channels" : g}>
            {items.map((p) => {
              const flag = Permission[p.key];
              const on = (draft.permissions & flag) === flag;
              const lacking = !admin && (mine & flag) !== flag && !on;
              const locked = !canEdit || lacking || (role.isDefault && p.key === "ADMINISTRATOR");
              return (
                <SettingsRow key={p.key} title={p.label} description={lacking ? `${p.description} You don't have this permission.` : p.description} htmlFor={`perm-${p.key}`}>
                  <Switch
                    id={`perm-${p.key}`}
                    label={p.label}
                    disabled={locked}
                    checked={on}
                    onCheckedChange={(v) => setDraft({ ...draft, permissions: v ? draft.permissions | flag : draft.permissions & ~flag })}
                  />
                </SettingsRow>
              );
            })}
          </SettingsGroup>
        );
      })}
      {list.length === 0 && <p className="py-6 text-center text-sm text-fg-3">No permissions match.</p>}
    </div>
  );
}

function MembersTab({ role, structure, canEdit }: { role: CommunityRole; structure: CommunityStructure; canEdit: boolean }) {
  const qc = useQueryClient();
  const refresh = useRefreshCommunity(structure.community.id);
  const { data, isPending } = useMembers(structure.community.id);
  const [q, setQ] = useState("");
  const all = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const holders = all.filter((m) => m.roleIds.includes(role.id));
  const candidates = q.trim()
    ? all.filter((m) => !m.roleIds.includes(role.id) && `${m.user.displayName} ${m.user.username}`.toLowerCase().includes(q.toLowerCase())).slice(0, 5)
    : [];

  const setRoles = async (m: Member, add: boolean) => {
    const roleIds = add ? [...m.roleIds, role.id] : m.roleIds.filter((r) => r !== role.id);
    try {
      await api.put(`/api/communities/${structure.community.id}/members/${m.user.id}/roles`, { roleIds });
      await qc.invalidateQueries({ queryKey: ["members", structure.community.id] });
      void refresh();
      if (add) setQ("");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (role.isDefault) return <p className="text-sm text-fg-3">Every member has @everyone.</p>;
  // An app's role belongs to its bot alone.
  const managed = role.managed;
  return (
    <div>
      {managed && (
        <p className="mb-4 flex items-start gap-2 rounded-lg bg-surface px-3 py-2.5 text-sm text-fg-2">
          <IconBot className="icon mt-0.5 size-4 shrink-0 text-fg-3" aria-hidden />
          This role belongs to an app's bot. It can't be given to anyone else, and it's removed with the app.
        </p>
      )}
      {canEdit && !managed && (
        <div className="relative mb-4">
          <IconSearch className="icon pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add members" aria-label="Add members to this role" className="!h-9 pl-8" />
          {candidates.length > 0 && (
            <ul className="absolute inset-x-0 top-full z-10 mt-1 rounded-lg bg-surface p-1 shadow-pop">
              {candidates.map((m) => (
                <li key={m.user.id}>
                  <button onClick={() => void setRoles(m, true)} className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-surface-2">
                    <Avatar src={m.user.avatarUrl} name={m.user.displayName} size="xs" />
                    <span className="truncate text-sm">{m.user.displayName}</span>
                    <span className="truncate text-xs text-fg-3">@{m.user.username}</span>
                    <IconPlus className="icon ml-auto size-4 text-fg-3" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <SettingsGroup title={`${holders.length} ${holders.length === 1 ? "member" : "members"}`}>
        {holders.map((m) => (
          <div key={m.user.id} className="flex items-center gap-3 px-4 py-2">
            <Avatar src={m.user.avatarUrl} name={m.user.displayName} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{m.user.displayName}</div>
              <div className="truncate text-xs text-fg-3">@{m.user.username}</div>
            </div>
            {canEdit && !managed && <IconButton size="sm" label={`Remove ${m.user.displayName} from ${role.name}`} icon={IconUserMinus} tone="danger" onClick={() => void setRoles(m, false)} />}
          </div>
        ))}
        {!isPending && holders.length === 0 && <p className="px-4 py-3 text-sm text-fg-3">No one has this role yet.</p>}
      </SettingsGroup>
    </div>
  );
}

/* ───────────── Section ───────────── */

interface Draft {
  name: string;
  color: string | null;
  permissions: number;
  hoist: boolean;
}

function RoleEditor({ structure, role, canEdit }: { structure: CommunityStructure; role: CommunityRole; canEdit: boolean }) {
  const refresh = useRefreshCommunity(structure.community.id);
  const source: Draft = { name: role.name, color: role.color, permissions: role.permissions, hoist: role.hoist };
  const [draft, setDraft] = useState<Draft>(source);
  const [tab, setTab] = useState<"display" | "permissions" | "members">(role.isDefault ? "permissions" : "display");
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(source), [role.id, role.name, role.color, role.permissions, role.hoist]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(draft) !== JSON.stringify(source);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/roles/${role.id}`, role.isDefault ? { permissions: draft.permissions } : draft);
      await refresh();
      toast.show("Role saved");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    const ok = await confirm({ title: `Delete ${role.name}?`, description: `${role.memberCount ?? 0} ${role.memberCount === 1 ? "member" : "members"} will lose it.`, confirmLabel: "Delete role", danger: true });
    if (!ok) return;
    try {
      await api.del(`/api/roles/${role.id}`);
      await refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="min-w-0 flex-1">
      <div className="mb-5 flex items-center gap-3">
        <span className="size-3.5 shrink-0 rounded-full" style={{ background: draft.color ?? "var(--fg-3)" }} aria-hidden />
        <h3 className="min-w-0 flex-1 truncate text-lg font-semibold">{role.isDefault ? "@everyone" : draft.name || "Untitled role"}</h3>
        {!canEdit && (
          <span className="flex items-center gap-1 text-xs text-fg-3">
            <IconLock className="icon size-3.5" aria-hidden /> Above your highest role
          </span>
        )}
        {role.managed && (
          <span className="flex items-center gap-1 rounded bg-surface-2 px-1.5 py-0.5 text-xs font-medium text-fg-2">
            <IconBot className="icon size-3.5" aria-hidden /> App
          </span>
        )}
        {canEdit && !role.isDefault && !role.managed && (
          <Menu>
            <MenuTrigger asChild>
              <IconButton label="Role options" icon={IconMore} />
            </MenuTrigger>
            <MenuContent>
              <MenuItem icon={IconTrash} danger onSelect={() => void remove()}>
                Delete role
              </MenuItem>
            </MenuContent>
          </Menu>
        )}
      </div>
      <Segmented
        ariaLabel="Role settings"
        value={tab}
        onChange={setTab}
        className="mb-6"
        options={[
          ...(role.isDefault ? [] : [{ value: "display" as const, label: "Display" }]),
          { value: "permissions" as const, label: "Permissions" },
          ...(role.isDefault ? [] : [{ value: "members" as const, label: `Members${role.memberCount ? ` · ${role.memberCount}` : ""}` }]),
        ]}
      />
      {tab === "display" && <DisplayTab role={role} draft={draft} setDraft={setDraft} canEdit={canEdit} />}
      {tab === "permissions" && <PermissionsTab role={role} structure={structure} draft={draft} setDraft={setDraft} canEdit={canEdit} />}
      {tab === "members" && <MembersTab role={role} structure={structure} canEdit={canEdit} />}
      <UnsavedBar show={dirty && canEdit} saving={saving} onReset={() => setDraft(source)} onSave={() => void save()} />
    </div>
  );
}

function RoleRow({
  role: r,
  selected,
  onSelect,
  locked,
  dragging,
  grip,
  onKeyDown,
}: {
  role: CommunityRole;
  selected: boolean;
  onSelect: () => void;
  locked: boolean;
  dragging?: boolean;
  grip?: React.ReactNode;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
}) {
  return (
    <div
      className={cn(
        "group flex h-9 w-full items-center gap-2 rounded-md px-2 text-sm",
        dragging ? "bg-surface-3 text-fg" : selected ? "bg-surface-2 text-fg" : "text-fg-2 hover:bg-surface hover:text-fg",
      )}
    >
      {grip ?? <span className="w-4 shrink-0" />}
      <button
        data-sort-handle
        onClick={onSelect}
        onKeyDown={onKeyDown}
        aria-current={selected ? "true" : undefined}
        className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left focus-visible:outline-offset-0"
      >
        <span className="size-2.5 shrink-0 rounded-full" style={{ background: r.color ?? "var(--fg-3)" }} aria-hidden />
        <span className="min-w-0 flex-1 truncate">{r.name}</span>
        {r.managed && (
          <Tooltip label="Managed by an app">
            <span className="inline-flex shrink-0" role="img" aria-label="Managed by an app">
              <IconBot className="icon size-3.5 text-fg-3" aria-hidden />
            </span>
          </Tooltip>
        )}
        {(r.permissions & Permission.ADMINISTRATOR) !== 0 && <IconShield className="icon size-3.5 text-fg-3" aria-label="Administrator" />}
        {locked && <IconLock className="icon size-3.5 text-fg-3" aria-label="Above your highest role" />}
        <span className="flex items-center gap-0.5 text-xs text-fg-3 tabular">
          <IconUsers className="icon size-3.5" aria-hidden />
          {r.memberCount ?? 0}
        </span>
      </button>
    </div>
  );
}

export function RolesSection({ structure, sub, go }: SectionProps) {
  const qc = useQueryClient();
  const { community, roles } = structure;
  const [createOpen, setCreateOpen] = useState(false);
  const [q, setQ] = useState("");
  const myTop = myTopPosition(structure);
  const selected = roles.find((r) => r.id === sub) ?? roles.find((r) => !r.isDefault) ?? roles[0];
  const ordered = roles.filter((r) => !r.isDefault); // highest first
  const everyone = roles.find((r) => r.isDefault);
  const canMove = (r: CommunityRole) => !r.isDefault && (community.isOwner || r.position < myTop);
  const visible = ordered.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));

  const commitOrder = async (ids: string[]) => {
    // Optimistic: reorder in the cached structure.
    qc.setQueryData<CommunityStructure>(["structure", community.id], (old) => {
      if (!old) return old;
      const byId = new Map(old.roles.map((r) => [r.id, r]));
      const top = ids.map((id, i) => ({ ...byId.get(id)!, position: ids.length - i }));
      return { ...old, roles: [...top, ...old.roles.filter((r) => r.isDefault)] };
    });
    try {
      await api.put(`/api/communities/${community.id}/roles/order`, { ids });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      void qc.invalidateQueries({ queryKey: ["structure", community.id] });
    }
  };

  const move = (id: string, toIndex: number) => {
    const ids = ordered.map((r) => r.id).filter((x) => x !== id);
    ids.splice(Math.max(0, Math.min(ids.length, toIndex)), 0, id);
    // Roles above the actor must keep their place; refuse moves that would cross them.
    const crossing = ordered.some((r, i) => !canMove(r) && ids.indexOf(r.id) !== i);
    if (crossing) return toast.error("You can only reorder roles below your highest role");
    void commitOrder(ids);
  };

  return (
    <div>
      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="shrink-0 lg:w-64">
          <div className="mb-3 flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roles" aria-label="Search roles" className="!h-9" />
            <IconButton label="Create role" icon={IconPlus} className="border border-line-strong" onClick={() => setCreateOpen(true)} />
          </div>
          <p className="mb-2 px-1 text-xs text-fg-3">Higher roles outrank lower ones. Drag the handle to reorder, or press Alt + ↑/↓.</p>
          {q ? (
            <ul className="space-y-0.5" aria-label="Matching roles">
              {visible.map((r) => (
                <li key={r.id}>
                  <RoleRow role={r} selected={selected?.id === r.id} onSelect={() => go("roles", r.id)} locked={!canMove(r)} />
                </li>
              ))}
            </ul>
          ) : (
            <SortableList
              label="Roles, highest first"
              className="space-y-0.5"
              items={ordered}
              getId={(r) => r.id}
              canMove={canMove}
              onMove={move}
              renderItem={(r, { handle, dragging, movable }) => (
                <RoleRow
                  role={r}
                  selected={selected?.id === r.id}
                  onSelect={() => go("roles", r.id)}
                  locked={!movable}
                  dragging={dragging}
                  onKeyDown={handle.onKeyDown}
                  grip={
                    movable && (
                      <span
                        onPointerDown={handle.onPointerDown}
                        style={handle.style}
                        aria-hidden
                        className="-my-1 -ml-1 flex h-8 w-5 shrink-0 items-center justify-center text-fg-3 opacity-100 transition-opacity hover:text-fg [@media(pointer:fine)]:opacity-0 [@media(pointer:fine)]:group-hover:opacity-100"
                      >
                        <IconDrag className="icon size-3.5" />
                      </span>
                    )
                  }
                />
              )}
            />
          )}
          {everyone && (
            <div className="mt-2 border-t border-line pt-2">
              <button
                onClick={() => go("roles", everyone.id)}
                aria-current={selected?.id === everyone.id ? "true" : undefined}
                className={cn("flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm", selected?.id === everyone.id ? "bg-surface-2 text-fg" : "text-fg-2 hover:bg-surface hover:text-fg")}
              >
                <span className="w-4" />
                <span className="size-2.5 shrink-0 rounded-full bg-fg-3" aria-hidden />
                <span className="flex-1">@everyone</span>
                <span className="text-xs text-fg-3">Base</span>
              </button>
            </div>
          )}
          {community.isOwner && (
            <p className="mt-3 flex items-center gap-1.5 px-1 text-xs text-fg-3">
              <IconCrown className="icon size-3.5 text-warning" aria-hidden /> As owner you're above every role.
            </p>
          )}
        </div>
        {selected && <RoleEditor key={selected.id} structure={structure} role={selected} canEdit={selected.isDefault ? true : canMove(selected)} />}
      </div>
      <CreateRoleDialog open={createOpen} onOpenChange={setCreateOpen} structure={structure} onCreated={(id) => go("roles", id)} />
    </div>
  );
}
